// @ts-check
// Rotas REST dos modelos de e-mail, da biblioteca de imagens e de "Identidade e envio" (spec/email.md seções 7.3, 8.6
// e 9.2; contrato 09 de e-mail, seções 6 a 9). No modo demonstração, client/src/data/mock-email-templates.js responde.
import { api } from './api-client.js';

/**
 * @typedef {import('../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {import('../../../shared/email-design/email-document.js').EmailEditorMode} EmailEditorMode
 * @typedef {'MARKETING' | 'SERVICE'} EmailPurpose
 * @typedef {{ code: string, path?: string, blockId?: string }} EmailCompileProblem
 * @typedef {{ id: string, name: string, description: string, category: string, purpose: EmailPurpose, editorMode: EmailEditorMode,
 *   subject: string, preheader: string, galleryKey: string | null, hasErrors: boolean, hasValidContent: boolean, createdAt: string,
 *   updatedAt: string, createdByWorkspaceMemberId: string | null, updatedByWorkspaceMemberId: string | null, archivedAt: string | null,
 *   usage: { flows: number, liveFlows: number } }} EmailTemplateSummary
 * @typedef {EmailTemplateSummary & { document: EmailDocument, canEdit?: boolean, compiled: { contentId: string | null, byteSize: number,
 *   errors: EmailCompileProblem[], warnings: EmailCompileProblem[], variables: string[],
 *   links: Array<{ index: number, label: string, url: string, isTracked: boolean }> } }} EmailTemplate
 * @typedef {{ key: string, category: string, purpose: EmailPurpose, editorMode: EmailEditorMode, subject: string, preheader: string,
 *   document: EmailDocument }} EmailGalleryItem
 * @typedef {{ path: string, label?: string, labelKey?: string, type?: string, parentLabel?: string }} EmailVariableEntry
 * @typedef {{ contact: EmailVariableEntry[], record: EmailVariableEntry[], institution: EmailVariableEntry[], special: EmailVariableEntry[] }} EmailVariableCatalog
 * @typedef {{ subject: string, preheader: string, html: string, text: string, links: Array<string | null>, byteSize: number,
 *   estimatedHeight: number, warnings: EmailCompileProblem[], errors: EmailCompileProblem[],
 *   sample: { cardId: string, cardName: string, contactName: string } | null }} EmailPreview
 * @typedef {{ id: string, source: string, fileName: string, mimeType: string, extension: 'png' | 'jpg' | 'gif', byteSize: number,
 *   width: number | null, height: number | null, createdAt: string, isSent: boolean, purgedAt: string | null, url: string }} EmailAsset
 * @typedef {{ count: number, byteSize: number, maximumCount: number, maximumByteSize: number }} EmailAssetUsage
 * @typedef {{ brandColor: string, fromName: string, replyToEmail: string, footerAddress: string, footerPhone: string, footerWebsite: string,
 *   siteDomains: string[], marketingReasonText: string, serviceReasonText: string, trackOpens: boolean, trackClicks: boolean,
 *   dailyLimit: number | null }} EmailMarketingSettings
 * @typedef {{ settings: EmailMarketingSettings, canEdit: boolean, assets: EmailAssetUsage, platformDailyLimit: number,
 *   sendingAddress: string, platformSendingAddress?: string, replyToAddress: string,
 *   usageToday: { day: string, school: number, schoolLimit: number, schoolCampaigns: number, schoolCampaignsLimit: number } | null,
 *   eventsEnabled: boolean | null, suspension: { reason: string, at: string | null } | null, sendingDomain?: Record<string, any> | null }} EmailSettingsResponse
 */

/** @param {any} body */
const dataOf = (body) => body?.data ?? {};

/**
 * @param {{ search?: string, category?: string, mode?: string, purpose?: string, archived?: boolean }} [query]
 * @returns {Promise<EmailTemplateSummary[]>}
 */
export async function listEmailTemplates(query = {}) {
  const body = await api.get('/rest/email-templates', {
    search: query.search, category: query.category, mode: query.mode, purpose: query.purpose, archived: query.archived ? '1' : undefined,
  });
  return dataOf(body).templates ?? [];
}

/** @param {string} templateId @returns {Promise<EmailTemplate>} */
export async function getEmailTemplate(templateId) {
  return dataOf(await api.get(`/rest/email-templates/${encodeURIComponent(templateId)}`)).template;
}

/**
 * Modelo novo: em branco num modo (`editorMode` e, no HTML importado, `document`), da galeria (`galleryKey`) ou cópia
 * (`fromTemplateId`).
 * @param {{ name?: string, editorMode?: EmailEditorMode, document?: EmailDocument, galleryKey?: string, fromTemplateId?: string,
 *   purpose?: EmailPurpose, category?: string, subject?: string, preheader?: string }} input
 * @returns {Promise<EmailTemplate>}
 */
export async function createEmailTemplate(input) {
  return dataOf(await api.post('/rest/email-templates', input)).template;
}

/**
 * @param {string} templateId
 * @param {{ name?: string, description?: string, category?: string, purpose?: EmailPurpose, subject?: string, preheader?: string,
 *   document?: EmailDocument, expectedUpdatedAt?: string }} patch
 * @returns {Promise<EmailTemplate>}
 */
export async function updateEmailTemplate(templateId, patch) {
  return dataOf(await api.patch(`/rest/email-templates/${encodeURIComponent(templateId)}`, patch)).template;
}

/** @param {string} templateId */
export async function deleteEmailTemplate(templateId) {
  return dataOf(await api.delete(`/rest/email-templates/${encodeURIComponent(templateId)}`));
}

/** @param {string} templateId @param {boolean} archived @returns {Promise<EmailTemplate>} */
export async function setEmailTemplateArchived(templateId, archived) {
  return dataOf(await api.post(`/rest/email-templates/${encodeURIComponent(templateId)}/${archived ? 'archive' : 'unarchive'}`)).template;
}

/** @param {string} templateId @param {{ name?: string }} [input] @returns {Promise<EmailTemplate>} */
export async function duplicateEmailTemplate(templateId, input = {}) {
  return dataOf(await api.post(`/rest/email-templates/${encodeURIComponent(templateId)}/duplicate`, input)).template;
}

/** @param {string} templateId @param {EmailEditorMode} toMode @returns {Promise<EmailTemplate>} */
export async function convertEmailTemplate(templateId, toMode) {
  return dataOf(await api.post(`/rest/email-templates/${encodeURIComponent(templateId)}/convert`, { toMode })).template;
}

/** @param {string} templateId @returns {Promise<{ flows: Array<{ id: string | null, name: string | null, isLive: boolean }> }>} */
export async function listEmailTemplateUsages(templateId) {
  return dataOf(await api.get(`/rest/email-templates/${encodeURIComponent(templateId)}/usages`));
}

/** @returns {Promise<EmailGalleryItem[]>} */
export async function listEmailGallery() {
  return dataOf(await api.get('/rest/email-templates/gallery')).items ?? [];
}

/** @returns {Promise<EmailVariableCatalog>} */
export async function getEmailVariableCatalog() {
  const data = dataOf(await api.get('/rest/email-templates/variables'));
  return { contact: data.contact ?? [], record: data.record ?? [], institution: data.institution ?? [], special: data.special ?? [] };
}

/**
 * HTML de fora, colado ou num arquivo: volta saneado, com o relatório do que saiu, sem gravar nada.
 * @param {{ html: string } | { file: File }} input
 * @returns {Promise<{ document: EmailDocument, report: import('../../../shared/email-design/sanitize-email-html.js').SanitizeReport,
 *   warnings: Array<{ code: string }> }>}
 */
export async function importEmailHtml(input) {
  if ('file' in input) {
    const formData = new FormData();
    formData.append('file', input.file);
    return dataOf(await api.upload('/rest/email-templates/import-html', formData));
  }
  return dataOf(await api.post('/rest/email-templates/import-html', { html: input.html }));
}

/**
 * Prévia do servidor (com os dados de um card, lido com o papel de quem pede, ou os de exemplo).
 * @param {{ document: EmailDocument, subject: string, preheader: string, purpose: EmailPurpose, sampleCardId?: string | null }} input
 * @returns {Promise<EmailPreview>}
 */
export async function previewEmailDocument(input) {
  return dataOf(await api.post('/rest/email-templates/preview', input));
}

/**
 * E-mail de teste para quem pede e até 4 colegas (com `sampleCardId`, os colegas recebem os dados de exemplo).
 * @param {string} templateId @param {{ sampleCardId?: string | null, workspaceMemberIds?: string[] }} input
 * @returns {Promise<{ sentTo: string[], simulated?: boolean }>} simulated: modo demonstração
 */
export async function sendEmailTemplateTest(templateId, input) {
  return dataOf(await api.post(`/rest/email-templates/${encodeURIComponent(templateId)}/test`, input));
}

// Biblioteca de imagens

/**
 * @param {{ search?: string, startingAfter?: string | null, limit?: number }} [query]
 * @returns {Promise<{ assets: EmailAsset[], usage: EmailAssetUsage, pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listEmailAssets(query = {}) {
  const body = await api.get('/rest/email-assets', { search: query.search, starting_after: query.startingAfter ?? undefined, limit: query.limit });
  const data = dataOf(body);
  return { assets: data.assets ?? [], usage: data.usage, pageInfo: body?.pageInfo ?? { hasNextPage: false, endCursor: null } };
}

/** @param {File} file @returns {Promise<EmailAsset>} */
export async function uploadEmailAsset(file) {
  const formData = new FormData();
  formData.append('file', file);
  return dataOf(await api.upload('/rest/email-assets', formData)).asset;
}

/** @param {string} assetId @returns {Promise<{ id: string, hiddenOnly: boolean }>} */
export async function deleteEmailAsset(assetId) {
  return dataOf(await api.delete(`/rest/email-assets/${encodeURIComponent(assetId)}`));
}

/** "Remover do ar" (flag SECURITY): o endereço público passa a responder 410. @param {string} assetId */
export async function purgeEmailAsset(assetId) {
  return dataOf(await api.post(`/rest/email-assets/${encodeURIComponent(assetId)}/purge`, { confirm: true })).asset;
}

// Identidade e envio

/** @returns {Promise<EmailSettingsResponse>} */
export async function getEmailSettings() {
  return dataOf(await api.get('/rest/email-settings'));
}

/** @param {Partial<EmailMarketingSettings>} patch @returns {Promise<{ settings: EmailMarketingSettings, changed: string[] }>} */
export async function updateEmailSettings(patch) {
  return dataOf(await api.patch('/rest/email-settings', patch));
}
